import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import Widget from '@jimmyboys/liita-ui/dist/components/Widget'
import SwitchButton from '@jimmyboys/liita-ui/dist/components/SwitchButton'
import NoData from 'components/general/NoData/NoData'
import React, { useEffect, useReducer, useState } from 'react'
import { useDeepCompareEffect } from 'react-use'
import { useFetchList } from 'store'
import MultiSelect from '@jimmyboys/liita-ui/dist/components/MultiSelect'
import Dropdown from '@jimmyboys/liita-ui/dist/components/Dropdown'

import { Options } from 'models'
import { API_URL, fetchOptions } from 'store/fetchUtils'
import { useReload } from 'store'

import styles from './CoordinatorAllocationRules.module.sass'

const reducer = (state, action) => {
	switch (action.type) {
		// case 'addRule':
		// 	return [
		// 		...state,
		// 		{
		// 			key: Date.now(),
		// 			enabled: true,
		// 			bodyPart: [],
		// 			processType: [],
		// 			insuranceCompany: [],
		// 		},
		// 	]
		case 'removeRule':
			return [
				...state.slice(0, action.payload),
				...state.slice(action.payload + 1),
			]
		case 'updateRule':
			return [
				...state.slice(0, action.payload.index),
				{
					...state[action.payload.index],
					[action.payload.prop]: action.payload.value,
				},
				...state.slice(action.payload.index + 1),
			]
		case 'swap': {
			let firstIndex = state.findIndex((r) => r.key === action.payload[0])
			let secondIndex = state.findIndex(
				(r) => r.key === action.payload[1]
			)
			if (
				firstIndex >= 0 &&
				secondIndex >= 0 &&
				secondIndex !== firstIndex
			) {
				const item = state[firstIndex]
				const nextState = [
					...state.slice(0, firstIndex),
					...state.slice(firstIndex + 1),
				]
				return [
					...nextState.slice(0, secondIndex),
					item,
					...nextState.slice(secondIndex),
				]
			}
			return state
		}
		case 'reset':
			return action.payload
		default:
			throw new Error()
	}
}

const CoordinatorAllocationRules = () => {
	const { i18n, expireSession } = useGlobal()
	const reload = useReload()
	const { state, contents } = useFetchList(
		[
			'/rules',
			'/metadata/body_parts',
			'/metadata/insurance_companies',
			'/metadata/process_types',
			'/users',
		],
		[
			(json) => json,
			(json) => {
				return {
					...Options.fromSimpleObjectJSON({
						id: json.code,
						name: json.name,
					}),
				}
			},
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
			(json) => ({
				...Options.fromSimpleObjectJSON({
					id: json.externalUserId,
					name: json.name,
				}),
				canBeCoordinator: json.roles.includes('coordenacao'),
			}),
		]
	)
	const [dragItem, setDragItem] = useState(null)
	const [busy, setBusy] = useState(false)
	const [rulesState, dispatch] = useReducer(reducer, [])
	const isLoading = state === 'loading'
	const rulesData = Array.isArray(contents?.[0]) ? contents[0] || [] : []
	const bodyPartsOptions = Array.isArray(contents?.[1]) ? contents[1] : []
	const insuranceCompaniesOptions = Array.isArray(contents?.[2])
		? contents[2]
		: []
	const processTypesOptions = Array.isArray(contents?.[3]) ? contents[3] : []
	let usersOptions = Array.isArray(contents?.[4]) ? contents[4] : []
	usersOptions = usersOptions.filter((user) => user.canBeCoordinator)

	useEffect(() => {
		const handleDrop = () => {
			setDragItem(null)
		}
		const handleDragOver = (e) => {
			e.preventDefault()
		}
		document.addEventListener('dragover', handleDragOver)
		document.addEventListener('dragend', handleDrop)
		document.addEventListener('drop', handleDrop)
		return () => {
			document.removeEventListener('dragend', handleDrop)
			document.removeEventListener('drop', handleDrop)
			document.removeEventListener('dragover', handleDragOver)
			setDragItem(null)
		}
	}, [])

	useDeepCompareEffect(() => {
		const rules = [...rulesData]
			.sort((a, b) => a.order - b.order)
			.map((json) => {
				return {
					id: json._id,
					key: json._id,
					enabled: json.enabled,
					userIds: json.userIds.map((userId) => {
						return {
							value: userId,
							text: usersOptions.find(
								({ value }) => value === userId
							)?.text,
						}
					}),
					bodyPart:
						json.bodyPart && json.bodyPart.value?.length
							? json.bodyPart.value.map((bpValue) => {
									return {
										value: bpValue,
										text: bodyPartsOptions.find(
											({ value }) => value === bpValue
										)?.text,
										$isSelected:
											json.bodyPart.type === 'in'
												? true
												: undefined,
										$isNegative:
											json.bodyPart.type === 'notIn'
												? true
												: undefined,
									}
							  })
							: [],
					processType:
						json.processType && json.processType.value?.length
							? json.processType.value.map((bpValue) => {
									return {
										value: bpValue,
										text: processTypesOptions.find(
											({ value }) => value === bpValue
										)?.text,
										$isSelected:
											json.processType.type === 'in'
												? true
												: undefined,
										$isNegative:
											json.processType.type === 'notIn'
												? true
												: undefined,
									}
							  })
							: [],
					insuranceCompany:
						json.insuranceCompany &&
						json.insuranceCompany.value?.length
							? json.insuranceCompany.value.map((bpValue) => {
									return {
										value: bpValue,
										text: insuranceCompaniesOptions.find(
											({ value }) => value === bpValue
										)?.text,
										$isSelected:
											json.insuranceCompany.type === 'in'
												? true
												: undefined,
										$isNegative:
											json.insuranceCompany.type ===
											'notIn'
												? true
												: undefined,
									}
							  })
							: [],
				}
			})
		dispatch({ type: 'reset', payload: rules })
	}, [rulesData])

	const handleSave = () => {
		if (busy) return
		const rules = rulesState.map((rule, index) => {
			let bodyPart
			let insuranceCompany
			let processType
			if (rule.bodyPart?.length) {
				const onlyPositives = !rule.bodyPart?.find?.(
					({ $isNegative }) => $isNegative
				)
				if (onlyPositives) {
					bodyPart = {
						type: 'in',
						value: rule.bodyPart
							.filter(({ $isNegative }) => !$isNegative)
							.map(({ value }) => value),
					}
				} else {
					bodyPart = {
						type: 'notIn',
						value: rule.bodyPart
							.filter(({ $isNegative }) => $isNegative)
							.map(({ value }) => value),
					}
				}
			}
			if (rule.insuranceCompany?.length) {
				const onlyPositives = !rule.insuranceCompany?.find?.(
					({ $isNegative }) => $isNegative
				)
				if (onlyPositives) {
					insuranceCompany = {
						type: 'in',
						value: rule.insuranceCompany
							.filter(({ $isNegative }) => !$isNegative)
							.map(({ value }) => value),
					}
				} else {
					insuranceCompany = {
						type: 'notIn',
						value: rule.insuranceCompany
							.filter(({ $isNegative }) => $isNegative)
							.map(({ value }) => value),
					}
				}
			}
			if (rule.processType?.length) {
				const onlyPositives = !rule.processType?.find?.(
					({ $isNegative }) => $isNegative
				)
				if (onlyPositives) {
					processType = {
						type: 'in',
						value: rule.processType
							.filter(({ $isNegative }) => !$isNegative)
							.map(({ value }) => value),
					}
				} else {
					processType = {
						type: 'notIn',
						value: rule.processType
							.filter(({ $isNegative }) => $isNegative)
							.map(({ value }) => value),
					}
				}
			}
			const userIds = rule.userIds?.length
				? rule.userIds.map(({ value }) => value)
				: undefined
			return {
				id: rule.id,
				order: index,
				enabled: rule.enabled,
				bodyPart,
				insuranceCompany,
				processType,
				userIds,
			}
		})
		if (rules.length) {
			setBusy(true)
			return fetch(
				`${API_URL}/api/rules`,
				fetchOptions({
					method: 'PUT',
					body: { rules },
				})
			)
				.then(() => {
					setBusy(false)
					reload(['/rules'])
				})
				.catch((e) => {
					if (e.status === '401' || e.status === 401) {
						expireSession()
					}

					setBusy(false)
				})
		}
	}

	const handleAddRule = async (e) => {
		e.preventDefault()
		if (busy) return
		const createNewRule = () => {
			setBusy(true)
			fetch(
				`${API_URL}/api/rules`,
				fetchOptions({
					method: 'POST',
					body: {
						enabled: false,
						order: rulesData.length,
					},
				})
			)
				.then(() => {
					setBusy(false)
					reload(['/rules'])
				})
				.catch((e) => {
					if (e.status === '401' || e.status === 401) {
						expireSession()
					}

					setBusy(false)
				})
		}
		const saveBeforeContinue = handleSave()
		if (saveBeforeContinue) {
			saveBeforeContinue
				.then(() => {
					createNewRule()
				})
				.catch((_) => {})
		} else {
			createNewRule()
		}
	}

	const handleDelete = (id) => {
		if (busy) return
		setBusy(true)
		fetch(
			`${API_URL}/api/rules/${id}`,
			fetchOptions({
				method: 'DELETE',
			})
		)
			.then(() => {
				setBusy(false)
				reload(['/rules'])
			})
			.catch((e) => {
				if (e.status === '401' || e.status === 401) {
					expireSession()
				}

				setBusy(false)
			})
	}

	return (
		<>
			<Widget paddings={['small', 'normal', '']} applyMaxWidth>
				<HStack justify='space-between'>
					<small>
						<strong>{i18n`Coordinator allocation rules`}</strong>
					</small>
					<ActionButton onClick={handleSave}>
						<small>{i18n`Save`}</small>
					</ActionButton>
				</HStack>
			</Widget>
			<Widget paddings={['small', 'normal', '']} applyMaxWidth>
				{!isLoading && !rulesState.length ? (
					<>
						<NoData
							applyMaxWidth
							paddings={['normal', 'normal']}
							message={i18n`The are no rules created for coordinator allocation.`}
						/>
						<HStack justify='center'>
							<ActionButton onClick={handleAddRule}>
								<small>{i18n`Add a rule`}</small>
							</ActionButton>
						</HStack>
					</>
				) : null}
				{rulesState.map((rule, index) => {
					return (
						<HStack
							className={`${styles['rule-entry']} ${
								dragItem === rule.key
									? styles['--is-dragging']
									: ''
							}`}
							draggable
							onDragStart={(e) => {
								setDragItem(rule.key)
							}}
							onDragEnter={(e) => {
								if (dragItem !== rule.key) {
									dispatch({
										type: 'swap',
										payload: [dragItem, rule.key],
									})
								}
							}}
							onDragOver={(e) => {
								e.preventDefault()
							}}
							onDrop={(_) => {
								setDragItem(null)
							}}
							key={rule.key}
							columns={['48px', 1, 1]}
							flex={[
								[0, 0],
								[1, 1],
							]}
							gap='small'
						>
							<VStack gap='xsmall' justify='space-between'>
								<VStack gap='xsmall'>
									<span
										style={{
											cursor: 'move',
											fill: 'white',
										}}
									>
										<Icon
											name='drag'
											width='14px'
											height='14px'
										/>
									</span>
									<SwitchButton
										checked={rule.enabled}
										onChange={(e) => {
											dispatch({
												type: 'updateRule',
												payload: {
													index,
													prop: 'enabled',
													value: e.target.checked,
												},
											})
										}}
									/>
								</VStack>
								<ActionButton
									className={styles['delete-button']}
									danger
									onClick={() => {
										handleDelete(rule.id)
									}}
								>
									<small>{i18n`Delete`}</small>
								</ActionButton>
							</VStack>
							<VStack gap='xsmall'>
								<small>{i18n`Conditions`}</small>
								<MultiSelect
									placeholder={i18n`Process type`}
									values={rule.processType}
									options={processTypesOptions}
									withExclusion={true}
									onChange={(e) => {
										dispatch({
											type: 'updateRule',
											payload: {
												index,
												prop: 'processType',
												value: e.target.value,
											},
										})
									}}
								/>
								<MultiSelect
									placeholder={i18n`Body part`}
									values={rule.bodyPart}
									options={bodyPartsOptions}
									withExclusion={true}
									searchable
									onChange={(e) => {
										dispatch({
											type: 'updateRule',
											payload: {
												index,
												prop: 'bodyPart',
												value: e.target.value,
											},
										})
									}}
								/>
								<MultiSelect
									placeholder={i18n`Insurance Company`}
									values={rule.insuranceCompany}
									options={insuranceCompaniesOptions}
									withExclusion={true}
									searchable
									onChange={(e) => {
										dispatch({
											type: 'updateRule',
											payload: {
												index,
												prop: 'insuranceCompany',
												value: e.target.value,
											},
										})
									}}
								/>
							</VStack>
							<VStack gap='xsmall'>
								<small>{i18n`Query`}</small>
								<Dropdown
									disabled
									value='userIds'
									options={[
										{ value: 'userIds', text: 'user id' },
									]}
								/>
								<MultiSelect
									placeholder={i18n`Coordinator`}
									values={rule.userIds}
									options={usersOptions}
									searchable
									onChange={(e) => {
										dispatch({
											type: 'updateRule',
											payload: {
												index,
												prop: 'userIds',
												value: e.target.value,
											},
										})
									}}
								/>
							</VStack>
						</HStack>
					)
				})}
			</Widget>
			{!isLoading && rulesState.length ? (
				<Widget paddings={['small', 'normal', '']} applyMaxWidth>
					<HStack justify='flex-end'>
						<ActionButton
							onClick={handleAddRule}
						>{i18n`Add a rule`}</ActionButton>
					</HStack>
				</Widget>
			) : null}
		</>
	)
}

export default CoordinatorAllocationRules
